// Preload compartido del visor de etiquetas. Reúne las dos operaciones
// costosas que antes arrancaban en secuencia después de montar el visor:
//   1) preloadPdfjs(): descarga pdf.js + su worker (chunk ~1 MB).
//   2) fetchLabelBlob(n): descarga el PDF de la etiqueta (publicDownloadLabel).
// El LabelViewerProvider las dispara al abrir el modal (mismo instante que se
// pinta el marco), en paralelo con la carga del chunk del visor. Así, cuando
// el visor monta, el PDF ya está descargándose y pdf.js ya está listo: el
// render del canvas arranca de inmediato en vez de esperar dos descargas
// secuenciales.

import { base44 } from '@/api/base44Client';
import { appParams } from '@/lib/app-params';

let pdfjsPromise = null;

// Carga pdf.js y su worker de forma diferida (code-split). Sin Web Worker: el
// módulo del worker se importa en el hilo principal y pdf.js lo usa como
// "fake worker" (globalThis.pdfjsWorker). Una etiqueta es de 1 página, así que
// el costo en el hilo principal es despreciable. Idempotente: la segunda
// llamada reutiliza la misma promesa.
export function preloadPdfjs() {
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = (async () => {
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
    const workerModule = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
    if (!globalThis.pdfjsWorker?.WorkerMessageHandler) globalThis.pdfjsWorker = workerModule;
    return pdfjs;
  })().catch((e) => { pdfjsPromise = null; throw e; });
  return pdfjsPromise;
}

// Descarga el PDF de la etiqueta como Blob (inyecta el token de sesión vía
// base44.functions.fetch). Devuelve { blob, isHtml }. Lanza en error HTTP o
// respuesta vacía para que el visor muestre el estado de error.
export async function fetchLabelBlob(n) {
  const fnPath = `/publicDownloadLabel?n=${encodeURIComponent(n)}&inline=1`;
  const headers = { Accept: 'application/pdf' };
  if (appParams.functionsVersion) headers['Base44-Functions-Version'] = appParams.functionsVersion;
  const r = await base44.functions.fetch(fnPath, { headers });
  if (!r.ok) throw new Error('HTTP ' + r.status);
  const blob = await r.blob();
  if (!blob || blob.size === 0) throw new Error('empty');
  const isHtml = (blob.type || '').includes('html');
  return { blob, isHtml };
}